import { useRef } from 'react'
import { useGSAP } from '@gsap/react'
import gsap from 'gsap'
import Image from 'next/image'

import Grid from '@/components/base/Grid'
import ImageParallax from '@/components/base/ImageParallax'
import Motion from '@/components/base/Motion'
import Section from '@/components/base/Section'
import SVGIcon from '@/components/base/SVGIcon'
import Typography from '@/components/base/Typography'
import { useLenis } from '@/contexts/lenis'
import { MediaQuery } from '@/constants'

import { lifestyleHeroRevealAnimation } from './animations'
import { cn } from '@/lib/utils'
import { WPImage } from '@/types'

export type LifestyleHeroProps = {
  image: WPImage
  paragraph: string
}

const LifestyleHero = ({ image, paragraph }: LifestyleHeroProps) => {
  const root = useRef<HTMLDivElement>(null!)
  const { lenis } = useLenis()

  useGSAP(
    (context) => {
      if (!lenis) return

      const selector = context?.selector || gsap.utils.selector(root.current)

      const matchMedia = gsap.matchMedia(root.current)

      matchMedia.add(MediaQuery.LARGE, () => {
        const logo = selector('[data-motion-logo]')
        const image = selector('[data-motion-image]')

        const scrollTrigger = {
          start: 0,
          scrub: true,
          markers: false,
          horizontal: true
        }

        gsap
          .timeline({ scrollTrigger: { ...scrollTrigger, trigger: logo } })
          .fromTo(logo, { xPercent: 0 }, { xPercent: 20, ease: 'none' })

        gsap
          .timeline({ scrollTrigger: { ...scrollTrigger, trigger: image } })
          .fromTo(image, { xPercent: 0 }, { xPercent: 10, ease: 'none' })
      })
    },
    { scope: root, dependencies: [lenis] }
  )

  return (
    <Section
      ref={root}
      className="mt-[11.2rem] md:mt-[15.2rem] lg:mt-0 lg:w-col-span-11"
    >
      <Motion animation={lifestyleHeroRevealAnimation}>
        {(motionRef) => (
          <div
            ref={motionRef}
            className="lg:absolute lg:left-0 lg:top-0 lg:h-full lg:w-full"
          >
            <Grid
              className="lg:absolute lg:left-0 lg:top-0 lg:z-10 lg:h-full lg:w-full"
              data-motion-logo
            >
              <div
                className={cn([
                  'col-span-2 md:col-span-6',
                  'lg:col-auto lg:row-span-4 lg:row-start-8 lg:w-col-span-4'
                ])}
              >
                <div className="mx-auto h-full w-full max-w-[20rem] overflow-hidden lg:mx-0 lg:max-w-full">
                  <SVGIcon
                    symbol="yves-logo-large"
                    className="aspect-[299/263] h-full w-full fill-text-heading-light lg:w-auto"
                    data-motion-icon
                  />
                </div>
              </div>
            </Grid>

            <Grid
              className="mt-spacing-lg lg:absolute lg:left-0 lg:top-0 lg:mt-0 lg:h-full lg:w-full"
              data-motion-image
            >
              <div
                className={cn([
                  'col-span-2 md:col-span-4 md:col-start-2',
                  'lg:col-auto lg:row-span-6 lg:row-start-3 lg:ml-col-start-3 lg:w-col-span-8'
                ])}
              >
                <div
                  className="relative aspect-square h-auto w-full overflow-hidden rounded-radius-rounded will-change-transform lg:aspect-auto lg:h-full lg:w-auto"
                  data-motion-wrapper
                >
                  <ImageParallax
                    className="h-full w-full rounded-radius-rounded will-change-transform lg:h-full lg:w-auto"
                    data-motion-content
                  >
                    {(ref) => (
                      <span className="absolute left-0 top-0 h-full w-full">
                        <Image
                          ref={ref}
                          src={image.url}
                          alt={image.alt}
                          priority={true}
                          fill
                          className="rounded-radius-rounded object-cover"
                        />
                      </span>
                    )}
                  </ImageParallax>
                </div>
              </div>
            </Grid>

            <Grid className="mt-spacing-lg lg:absolute lg:left-0 lg:top-0 lg:mt-0 lg:h-full lg:w-full">
              <div
                className={cn([
                  'col-span-2 md:col-span-4 md:col-start-2',
                  'lg:col-auto lg:row-span-10 lg:ml-col-start-7 lg:w-col-span-5',
                  'flex items-end'
                ])}
              >
                <Typography
                  variant="h5"
                  paragraph
                  className="capitalize lg:-mb-[0.75em] lg:-ml-[6rem]"
                  data-motion-paragraph
                >
                  {paragraph}
                </Typography>
              </div>
            </Grid>
          </div>
        )}
      </Motion>
    </Section>
  )
}

export default LifestyleHero
